import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordRules = [
    { label: '8 caractères minimum', ok: form.newPassword.length >= 8 },
    { label: '1 majuscule minimum', ok: /[A-Z]/.test(form.newPassword) },
    { label: '1 chiffre minimum', ok: /[0-9]/.test(form.newPassword) },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await resetPassword({ token, ...form });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Lien invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
            <p className="text-red-600 font-semibold mb-4">Lien de réinitialisation invalide.</p>
            <Link to="/mot-de-passe-oublie" className="text-yellow-500 font-bold">Demander un nouveau lien</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (success) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Mot de passe modifié !</h2>
            <p className="text-gray-600 mb-8">Votre mot de passe a été réinitialisé avec succès.</p>
            <button onClick={() => navigate('/connexion')} className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition">
              Se connecter
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔑</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h1>
            <p className="text-gray-500 text-sm">Choisissez un nouveau mot de passe sécurisé.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Nouveau mot de passe</label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'} value={form.newPassword}
                  onChange={e => setForm({ ...form, newPassword: e.target.value })} required
                  placeholder="••••••••"
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 pr-12 text-sm focus:border-black focus:outline-none transition"
                />
                <button type="button" onClick={() => setShowNew(!showNew)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showNew ? '🙈' : '👁'}
                </button>
              </div>
              {form.newPassword && (
                <div className="mt-2 flex gap-3 flex-wrap">
                  {passwordRules.map(rule => (
                    <span key={rule.label} className={`text-xs flex items-center gap-1 ${rule.ok ? 'text-green-600' : 'text-gray-400'}`}>
                      <span>{rule.ok ? '✓' : '○'}</span> {rule.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmer le mot de passe</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'} value={form.confirmPassword}
                  onChange={e => setForm({ ...form, confirmPassword: e.target.value })} required
                  placeholder="••••••••"
                  className={`w-full border-2 rounded-lg px-4 py-3 pr-12 text-sm focus:outline-none transition ${
                    form.confirmPassword && form.newPassword !== form.confirmPassword
                      ? 'border-red-400' : form.confirmPassword && form.newPassword === form.confirmPassword
                      ? 'border-green-400' : 'border-gray-200 focus:border-black'
                  }`}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showConfirm ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full bg-black text-white py-4 rounded-lg font-bold text-sm hover:bg-gray-800 transition disabled:opacity-50"
            >
              {loading ? 'Enregistrement...' : 'Enregistrer le nouveau mot de passe'}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </>
  );
}
